/**
 * A product's usual hours: its first leg's start and end, the times its card
 * shows as `Usual time`. Minutes past midnight.
 */
export type ProductUsualHours = {
  startMinutes: number;
  /** Null when the leg has no end, or one equal to its start. */
  endMinutes: number | null;
};

const clockMinutes = (value: unknown): number | null => {
  if (!value || typeof value !== 'object') return null;
  const { hours, minutes } = value as { hours?: unknown; minutes?: unknown };
  if (typeof hours !== 'number') return null;
  const mins = minutes ?? 0;
  if (typeof mins !== 'number') return null;
  if (!Number.isInteger(hours) || !Number.isInteger(mins)) return null;
  if (hours < 0 || hours > 23 || mins < 0 || mins > 59) return null;
  return hours * 60 + mins;
};

/**
 * Null when the product states no usual time. A leg left at 00:00 with no
 * other end is an unset form, not a midnight start — read as one, it would
 * put the product first in every day.
 */
export const productUsualHours = (legs: unknown): ProductUsualHours | null => {
  if (!Array.isArray(legs) || legs.length === 0) return null;
  const details = (legs[0] as { details?: unknown } | null)?.details as
    | { startTime?: unknown; endTime?: unknown }
    | undefined;
  const start = clockMinutes(details?.startTime);
  if (start === null) return null;
  const end = clockMinutes(details?.endTime);
  if (start === 0 && (end === null || end === 0)) return null;
  return { startMinutes: start, endMinutes: end === start ? null : end };
};

/** Minutes past midnight as `HH:MM`. */
export const usualHoursClock = (minutes: number): string =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
